<template>
  <b-row class="okr-ganhos-block match-height">
    <!-- skeletons mantêm o grid estável durante o load -->
    <template v-if="loading">
      <b-col
        v-for="n in skeletonCount"
        :key="`ganhos-skeleton-${n}`"
        cols="12"
        md="6"
        :lg="colSize"
      >
        <b-card class="ganhos-card" no-body>
          <b-card-body>
            <div class="skeleton-line w-50" />
            <div class="skeleton-line skeleton-big w-75" />
            <div class="skeleton-line" />
            <div class="skeleton-line" />
            <div class="skeleton-line w-75" />
          </b-card-body>
        </b-card>
      </b-col>
    </template>

    <template v-else>
      <!-- ── Bloco 1: Meta de Faturamento ─────────────────────── -->
      <b-col cols="12" md="6" :lg="colSize">
        <b-card class="ganhos-card" no-body>
          <b-card-body>
            <div class="block-title">Meta de Faturamento</div>

            <div class="main-value text-primary">
              {{ animatedRevenue | toCurrency }}
            </div>
            <div class="text-muted font-small-3 mb-75">Faturamento realizado no trimestre</div>

            <div class="derived-row">
              <span class="text-muted font-small-3">Meta do trimestre</span>
              <span class="font-weight-bolder">{{ data.total_revenue_target | toCurrency }}</span>
            </div>
            <div class="derived-row mb-50">
              <span class="text-muted font-small-3">Meta do mês vigente</span>
              <span class="font-weight-bolder">{{ data.monthly_revenue_target | toCurrency }}</span>
            </div>

            <b-progress
              :value="revenuePct"
              :max="100"
              height="6px"
              :variant="progressVariant(revenuePct)"
            />
            <div class="text-right font-small-2 text-muted mt-25">
              {{ revenuePct }}% da meta trimestral
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- ── Bloco 2: Pró-labore Projetado × Realizado (só Meu OKR) ── -->
      <b-col v-if="!isTeamScope" cols="12" md="6" :lg="colSize">
        <b-card class="ganhos-card" no-body>
          <b-card-body>
            <div class="block-title">
              Pró-labore Projetado × Realizado
              <feather-icon
                id="prolabore-info"
                icon="InfoIcon"
                size="13"
                class="text-muted ml-25 cursor-pointer"
              />
              <b-tooltip target="prolabore-info" triggers="hover" placement="top">
                Comparação do mês de referência ({{ referenceMonthLabel }}).
                Projetado = pró-labore trimestral do resumo do OKR ÷ 3.
              </b-tooltip>
            </div>

            <div class="text-muted font-small-2 mb-50">Mês de referência: {{ referenceMonthLabel }}</div>

            <div class="derived-row">
              <span class="text-muted font-small-3">Projetado no mês</span>
              <span class="font-weight-bolder">{{ data.monthly_prolabore_target | toCurrency }}</span>
            </div>
            <div class="derived-row">
              <span class="text-muted font-small-3">Realizado no mês</span>
              <span class="font-weight-bolder">{{ data.monthly_prolabore_realized | toCurrency }}</span>
            </div>
            <div class="derived-row mb-50">
              <span class="text-muted font-small-3">Diferença</span>
              <span :class="prolaboreDiff < 0 ? 'text-danger font-weight-bolder' : 'text-success font-weight-bolder'">
                {{ prolaboreDiff >= 0 ? '+' : '-' }}{{ Math.abs(prolaboreDiff) | toCurrency }}
              </span>
            </div>

            <b-progress
              :value="prolaborePct"
              :max="100"
              height="6px"
              :variant="progressVariant(prolaborePct)"
            />
            <div class="text-right font-small-2 text-muted mt-25">
              {{ prolaborePct }}% alcançado no mês
            </div>

            <div class="derived-row mt-50 quarter-line">
              <span class="text-muted font-small-2">Trimestre: realizado × meta</span>
              <span class="font-small-2 font-weight-bolder">
                {{ data.total_prolabore_realized | toCurrency }} / {{ data.total_prolabore_target | toCurrency }}
              </span>
            </div>
          </b-card-body>
        </b-card>
      </b-col>

      <!-- ── Bloco 3: widget destaque ─────────────────────────── -->
      <b-col cols="12" md="6" :lg="colSize">
        <b-card class="ganhos-card bonus-widget bg-gradient-primary text-white" no-body>
          <!-- Equipe direta: soma de metas e realizados dos membros -->
          <b-card-body v-if="isTeamScope">
            <div class="block-title text-white">EP</div>

            <div class="main-value text-white">
              {{ data.ep_realized || 0 }}
            </div>
            <div class="bonus-subtitle font-small-3 mb-75">EPs realizadas pela equipe direta</div>

            <div class="derived-row">
              <span class="bonus-subtitle font-small-3">Meta de EP da equipe</span>
              <span class="font-weight-bolder text-white">{{ data.ep_target || 0 }}</span>
            </div>
            <div class="derived-row mb-50">
              <span class="bonus-subtitle font-small-3">Faltam para a meta</span>
              <span class="font-weight-bolder text-white">{{ teamEpRemaining }}</span>
            </div>

            <b-progress
              :value="Math.min(data.ep_realized || 0, data.ep_target || 0)"
              :max="data.ep_target || 1"
              height="6px"
              class="bonus-progress"
            />
            <div class="text-right font-small-2 bonus-subtitle mt-25">
              {{ teamEpPct }}% da meta
            </div>
          </b-card-body>

          <b-card-body v-else>
            <feather-icon
              id="bonus-rules-info"
              icon="InfoIcon"
              size="16"
              class="bonus-info-icon cursor-pointer"
            />
            <b-popover target="bonus-rules-info" triggers="click blur" placement="leftbottom">
              <template #title>Regras do Bônus de EP</template>
              <div class="font-small-3">
                <div class="font-weight-bolder mb-25">Faixas de bônus</div>
                <div v-for="tier in bonusTiers" :key="tier.ep_target" class="d-flex justify-content-between">
                  <span>{{ tier.ep_target }} EPs</span>
                  <span class="font-weight-bolder">{{ tier.bonus_percentage }}%</span>
                </div>
                <hr class="my-50" />
                <div class="font-weight-bolder mb-25">Elegibilidade</div>
                <p class="mb-50">
                  Ao atingir a quantidade de EPs da faixa escolhida no seu OKR,
                  o bônus é aplicado sobre o faturamento de EP do trimestre.
                </p>
                <div class="font-weight-bolder mb-25">Período de apuração</div>
                <p class="mb-0">{{ quarter }}º trimestre de {{ year }}.</p>
              </div>
            </b-popover>

            <div class="block-title text-white">Bônus de EP</div>

            <div class="main-value text-white">
              {{ data.bonus_current | toCurrency }}
            </div>
            <div class="bonus-subtitle font-small-3 mb-75">Bônus calculado até o momento</div>

            <div class="derived-row">
              <span class="bonus-subtitle font-small-3">Bônus projetado no trimestre</span>
              <span class="font-weight-bolder text-white">{{ data.bonus_projected | toCurrency }}</span>
            </div>
            <div class="derived-row mb-50">
              <span class="bonus-subtitle font-small-3">EPs realizadas</span>
              <span class="font-weight-bolder text-white">
                {{ data.ep_realized || 0 }}<template v-if="data.bonus_target_ep_count"> de {{ data.bonus_target_ep_count }}</template>
              </span>
            </div>

            <b-progress
              v-if="data.bonus_target_ep_count > 0"
              :value="Math.min(data.ep_realized || 0, data.bonus_target_ep_count)"
              :max="data.bonus_target_ep_count"
              height="6px"
              class="bonus-progress"
            />

            <!-- Gamificação -->
            <div v-if="data.ep_remaining_for_bonus > 0" class="bonus-gamification mt-75">
              <feather-icon icon="TargetIcon" size="14" class="mr-25" />
              Faltam <strong class="mx-25">{{ data.ep_remaining_for_bonus }} EPs</strong> para desbloquear o próximo bônus!
            </div>
            <div v-else-if="data.bonus_target_ep_count > 0" class="bonus-gamification unlocked mt-75">
              <feather-icon icon="AwardIcon" size="14" class="mr-25" />
              Bônus desbloqueado! 🎉
            </div>
          </b-card-body>
        </b-card>
      </b-col>
    </template>

    <!-- slot: card de Ações e Filtros, mesma linha/altura -->
    <slot />
  </b-row>
</template>

<script>
import {
  BRow, BCol, BCard, BCardBody, BProgress, BTooltip, BPopover,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'
import { mapActions } from 'vuex'
import * as types from '../store/types'
import * as structureTypes from '@/constants/structure_options'

const MONTH_NAMES = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
]

export default {
  name: 'OkrGanhosBlock',
  components: {
    BRow, BCol, BCard, BCardBody, BProgress, BTooltip, BPopover, FeatherIcon,
  },

  props: {
    year:          { type: [String, Number], required: true },
    quarter:       { type: [String, Number], required: true },
    structureType: { type: String, required: true },
    consultantId:  { type: Number, default: null },
  },

  data() {
    return {
      loading: false,
      animatedRevenue: 0,
      bonusTiers: [],
      data: {
        total_revenue_target:       0,
        monthly_revenue_target:     0,
        total_revenue_realized:     0,
        total_prolabore_target:     0,
        monthly_prolabore_target:   0,
        monthly_prolabore_realized: 0,
        ep_prolabore_realized:      0,
        total_prolabore_realized:   0,
        reference_month:            null,
        bonus_projected:            0,
        bonus_current:              0,
        ep_realized:                null,
        ep_remaining_for_bonus:     null,
        bonus_target_ep_count:      0,
      },
    }
  },

  computed: {
    Math: () => Math,

    isTeamScope() {
      return this.structureType !== structureTypes.OWN
    },

    colSize() {
      return this.isTeamScope ? 4 : 3
    },

    skeletonCount() {
      return this.isTeamScope ? 2 : 3
    },

    teamEpRemaining() {
      return Math.max(0, (this.data.ep_target || 0) - (this.data.ep_realized || 0))
    },

    teamEpPct() {
      if (!this.data.ep_target) return 0
      return Math.min(100, Math.round(((this.data.ep_realized || 0) / this.data.ep_target) * 100))
    },

    referenceMonthLabel() {
      const m = Number(this.data.reference_month)
      return m >= 1 && m <= 12 ? MONTH_NAMES[m - 1] : 'mês vigente'
    },

    revenuePct() {
      if (!this.data.total_revenue_target) return 0
      return Math.min(100, Math.round((this.data.total_revenue_realized / this.data.total_revenue_target) * 100))
    },

    prolaboreDiff() {
      return (this.data.monthly_prolabore_realized || 0) - (this.data.monthly_prolabore_target || 0)
    },

    prolaborePct() {
      if (!this.data.monthly_prolabore_target) return 0
      return Math.min(100, Math.round((this.data.monthly_prolabore_realized / this.data.monthly_prolabore_target) * 100))
    },
  },

  watch: {
    year:          'load',
    quarter:       'load',
    structureType: 'load',
  },

  mounted() {
    this.load()
    this.loadBonusTiers()
  },

  methods: {
    ...mapActions({
      fetchStats:      types.GET_PRO_LABORE_TOTAL,
      fetchBonusTiers: types.GET_BONUS_TIERS,
    }),

    async load() {
      if (!this.year || !this.quarter) return
      this.loading = true
      try {
        const resp = await this.fetchStats({
          year:           this.year,
          quarter:        this.quarter,
          structure_type: this.structureType,
        })
        if (resp) {
          this.data = { ...this.data, ...resp }
          this.$nextTick(() => this.animateRevenue(resp.total_revenue_realized || 0))
        }
      } finally {
        this.loading = false
      }
    },

    async loadBonusTiers() {
      if (!this.consultantId || this.isTeamScope) return
      try {
        const resp = await this.fetchBonusTiers(this.consultantId)
        this.bonusTiers = resp.data.tiers || []
      } catch { /* popover fica sem faixas */ }
    },

    progressVariant(pct) {
      if (pct >= 100) return 'success'
      if (pct >= 60)  return 'primary'
      if (pct >= 30)  return 'warning'
      return 'danger'
    },

    animateRevenue(target) {
      const duration = 900
      const start = performance.now()
      const from = this.animatedRevenue
      const step = (now) => {
        const t = Math.min((now - start) / duration, 1)
        const ease = 1 - Math.pow(1 - t, 3)
        this.animatedRevenue = from + (target - from) * ease
        if (t < 1) requestAnimationFrame(step)
        else this.animatedRevenue = target
      }
      requestAnimationFrame(step)
    },
  },
}
</script>

<style scoped>
.ganhos-card {
  border-top: 3px solid var(--primary);
}
.block-title {
  font-weight: 700;
  margin-bottom: 0.5rem;
}
.main-value {
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.2;
}
.derived-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.12rem 0;
}
.quarter-line {
  border-top: 1px dashed #ebe9f1;
  padding-top: 0.4rem;
}

.bonus-widget {
  border-top: none;
  position: relative;
}
.bonus-subtitle {
  color: rgba(255, 255, 255, 0.75);
}
.bonus-info-icon {
  position: absolute;
  top: 0.75rem;
  right: 0.75rem;
  color: rgba(255, 255, 255, 0.85);
}
.bonus-progress {
  background: rgba(255, 255, 255, 0.25);
}
.bonus-progress ::v-deep .progress-bar {
  background-color: rgba(255, 255, 255, 0.9) !important;
}
.bonus-gamification {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.45rem 0.65rem;
  font-size: 0.85rem;
  font-weight: 600;
}
.bonus-gamification.unlocked {
  background: rgba(40, 199, 111, 0.35);
}
.cursor-pointer {
  cursor: pointer;
}

.skeleton-line {
  height: 14px;
  border-radius: 4px;
  margin-bottom: 0.75rem;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ganhosShimmer 1.2s infinite ease-in-out both;
}
.skeleton-big {
  height: 26px;
}
@keyframes ganhosShimmer {
  0%   { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.dark-layout .skeleton-line {
  background: linear-gradient(90deg, #2d3448 25%, #3b4253 50%, #2d3448 75%);
  background-size: 200% 100%;
}
.dark-layout .quarter-line {
  border-top-color: #3b4253;
}
</style>
